import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Form, notification } from "antd";
import { useSelector } from "react-redux";

import { IFonteSaldoDeContas, ISolicitacaoAtivacao, IContaSaldoDeContas } from "../../../../../interfaces";
import { ICreateFonte, IGetFontes, IUpdateContasFontes } from "../../interfaces";
import { stateGlobal } from "../../../../../../../../interfaces/Global";

import { EmitirErro } from "../../../../../../../../services/EmitirErro";

export interface IGroupedBancoItem {
    id: number;
    nome: string;
    imageUrl?: string | null;
    contas: IContaSaldoDeContas[];
}

export interface IUseEditarFontesModalProps {
    visible: boolean;
    onClose: () => void;
    solicitacao?: ISolicitacaoAtivacao | null;
    selectedContas: IContaSaldoDeContas[];
    onGetFontes?: (data: IGetFontes) => Promise<IFonteSaldoDeContas[] | null>;
    onCreateFonte?: (data: ICreateFonte) => Promise<IFonteSaldoDeContas | null>;
    onUpdateContasFontes?: (data: IUpdateContasFontes) => Promise<IContaSaldoDeContas[] | void>;
    onSuccess: (updatedContas?: IContaSaldoDeContas[]) => void;
}

export const useEditarFontesModal = ({
    visible,
    onClose,
    solicitacao,
    selectedContas,
    onGetFontes,
    onUpdateContasFontes,
    onSuccess,
}: IUseEditarFontesModalProps) => {
    const [form] = Form.useForm();
    const [isFonteFormVisible, setIsFonteFormVisible] = useState(false);
    const [isLoadingFontes, setIsLoadingFontes] = useState<boolean>(false);
    const [fontes, setFontes] = useState<IFonteSaldoDeContas[] | null>(null);
    const [isFonteSelectOpen, setIsFonteSelectOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const propsState = useSelector((state: stateGlobal) => state.global);
    const isLight = propsState.theme === 'light';

    const loadedFontesRef = useRef<boolean>(false);

    const groupedContasByBanco = useMemo<IGroupedBancoItem[]>(() => {
        const bancosMap = new Map<number, IGroupedBancoItem>();

        selectedContas.forEach((conta) => {
            const bancoId = conta.bancoId || conta.banco?.id || 0;
            const bancoNome = conta.banco?.nome || 'Banco';
            const bancoImageUrl =
                (conta.banco as any)?.imageUrl ||
                (conta.banco as any)?.img_url ||
                (conta.banco as any)?.image_url ||
                null;

            if (!bancosMap.has(bancoId)) {
                bancosMap.set(bancoId, {
                    id: bancoId,
                    nome: bancoNome,
                    imageUrl: bancoImageUrl,
                    contas: [],
                });
            }

            bancosMap.get(bancoId)!.contas.push(conta);
        });

        return Array.from(bancosMap.values());
    }, [selectedContas]);

    const handleCloseFonteForm = useCallback(() => {
        setIsFonteFormVisible(false);
    }, []);

    const handleClickAdicionarFonte = useCallback(() => {
        setIsFonteFormVisible(true);
        setIsFonteSelectOpen(false);
    }, []);

    const handleFonteCreated = useCallback((fonte: IFonteSaldoDeContas) => {
        setFontes((prevState) => {
            if (!prevState) {
                return [fonte];
            }
            return [...prevState, fonte];
        });

        const fontesValue = form.getFieldValue('fontes') || [];
        const novasFontes = [...fontesValue, fonte.id];
        form.setFieldValue('fontes', novasFontes);
        handleCloseFonteForm();
    }, [form, handleCloseFonteForm]);

    const handleClose = useCallback(() => {
        setIsFonteFormVisible(false);
        form.resetFields();
        onClose();
    }, [form, onClose]);

    const loadFontes = useCallback(async () => {
        try {
            setIsLoadingFontes(true);
            const fontesData = await onGetFontes?.({ solicitacaoId: solicitacao?.id }) || null;
            setFontes(fontesData);
            setIsLoadingFontes(false);
            loadedFontesRef.current = true;
        } catch (error) {
            setIsLoadingFontes(false);
            EmitirErro(error, true, 'loadFontes_EditarFontesModal_2039ujasdn');
        }
    }, [solicitacao, onGetFontes]);

    useEffect(() => {
        if (visible) {
            if (!loadedFontesRef.current) {
                loadFontes();
            }
            form.resetFields();
        }
    }, [visible, loadFontes, form]);

    const handleSubmit = useCallback(async () => {
        try {
            const values = await form.validateFields();

            if (!solicitacao || selectedContas.length < 2) {
                return;
            }

            setIsSubmitting(true);

            const contaIds = selectedContas.map((c) => c.id);
            const fonteIds = values.fontes;

            if (!fonteIds || fonteIds.length === 0) {
                notification.error({
                    message: 'Erro',
                    description: 'Informe pelo menos 1 fonte de recurso para a conta.',
                });
                return;
            }

            const updatedContas = await onUpdateContasFontes?.({
                solicitacaoId: solicitacao.id,
                contaIds,
                fonteIds,
            });

            notification.success({
                message: 'Sucesso',
                description: 'Fontes das contas atualizadas com sucesso!',
            });

            setIsSubmitting(false);
            onSuccess((updatedContas as IContaSaldoDeContas[]) || undefined);
            handleClose();
        } catch (error: any) {
            setIsSubmitting(false);
            if (error?.errorFields) {
                notification.error({
                    message: 'Erro',
                    description: 'Preencha os campos obrigatórios!',
                });
                return;
            }
            EmitirErro(error, true, 'updateContasFontes_submit_980123u4asdn');
        }
    }, [form, solicitacao, selectedContas, onUpdateContasFontes, onSuccess, handleClose]);

    return {
        form,
        isFonteFormVisible,
        isLoadingFontes,
        fontes,
        isFonteSelectOpen,
        setIsFonteSelectOpen,
        isSubmitting,
        isLight,
        groupedContasByBanco,
        handleCloseFonteForm,
        handleClickAdicionarFonte,
        handleFonteCreated,
        handleClose,
        handleSubmit,
    };
};
